import Pegi3 from "../assets/icons/3.webp";
import Pegi7 from "../assets/icons/7.webp";
import Pegi12 from "../assets/icons/12.webp";
import Pegi16 from "../assets/icons/16.webp";
import Pegi18 from "../assets/icons/18.webp";
import { requestPointerLockForPCGame } from "./gameplay";
import MobileBg from "../assets/images/bg_mobile.webp";
import TabletBg from "../assets/images/bg_tablet.webp";
import DesktopBg from "../assets/images/scan_qrcode.webp";
import MobileImage from "../assets/images/main_image_mobile_1x.webp";
import MobileImage2x from "../assets/images/main_image_mobile_2x.webp";
import MobileImage3x from "../assets/images/main_image_mobile_3x.webp";
import TabletImage from "../assets/images/main_image_tablet_1x.webp";
import TabletImage2x from "../assets/images/main_image_tablet_2x.webp";
import TabletImage3x from "../assets/images/main_image_tablet_3x.webp";
import ActivateBg from "../assets/images/activate_bg.webp";
import DesktopImage from "../assets/images/main_image_desktop_1x.webp";
import DesktopImage2x from "../assets/images/main_image_desktop_2x.webp";
import DesktopImage3x from "../assets/images/main_image_desktop_3x.webp";
import LandscapeMobile from "../assets/images/landscape_image_mobile_1x.webp";
import LandscapeMobile2x from "../assets/images/landscape_image_mobile_2x.webp";
import LandscapeMobile3x from "../assets/images/landscape_image_mobile_3x.webp";
import GooglePlay from "../assets/images/google-play.webp";
import GooglePlay2x from "../assets/images/google-play@2x.webp";
import GooglePlay3x from "../assets/images/google-play@3x.webp";
import Ios from "../assets/images/ios.webp";
import Ios2x from "../assets/images/ios@2x.webp";
import Ios3x from "../assets/images/ios@3x.webp";
import Microsoft from "../assets/images/microsoft.webp";
import Microsoft2x from "../assets/images/microsoft@2x.webp";
import Microsoft3x from "../assets/images/microsoft@3x.webp";
import Lg from "../assets/images/lg.webp";
import Lg2x from "../assets/images/lg@2x.webp";
import Lg3x from "../assets/images/lg@3x.webp";
import Samsung from "../assets/images/samsung.webp";
import Samsung2x from "../assets/images/samsung@2x.webp";
import Samsung3x from "../assets/images/samsung@3x.webp";
import Amazon from "../assets/images/amazon.webp";
import Amazon2x from "../assets/images/amazon@2x.webp";
import Amazon3x from "../assets/images/amazon@3x.webp";
import { TypePopup } from "../types/popup";

import { isIOS, isTizen, isWebos } from "../utils/platform";
import { ScreenSize } from "../store/slices/appState";

const YTB_PROXY_BASE_URL = "https://youtube-player.onmogaming.com";

export const isYouTubeUrl = (url: string): boolean => {
  return /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/.test(url);
};

const getYtbVideoId = (url: string) => {
  if (!url) return "";
  // Extract video ID from YouTube URL
  if (url.includes("youtube.com/embed/")) {
    return url.split("youtube.com/embed/")[1].split("?")[0];
  } else if (url.includes("youtu.be/")) {
    return url.split("youtu.be/")[1].split("?")[0];
  } else if (url.includes("youtube.com/watch")) {
    const urlParams = new URLSearchParams(url.split("?")[1]);
    return urlParams.get("v") || "";
  } else return "";
};

const YTB_THUMBNAIL_VARIANT = {
  default: "default", // 120x90, 4:3
  mq: "mqdefault", // 320x180, 16:9
  hq: "hqdefault", // 480x360, 4:3
  sd: "sddefault", // 640x480, 4:3
  maxres: "maxresdefault", // 1280x720, 16:9
} as const;

export type YtbThumbnailQuality = keyof typeof YTB_THUMBNAIL_VARIANT;

export const getYtbVideoThumbnail = (
  url: string,
  quality: YtbThumbnailQuality = "sd",
) => {
  const videoId = getYtbVideoId(url);
  if (!videoId) return url;
  return `https://img.youtube.com/vi/${videoId}/${YTB_THUMBNAIL_VARIANT[quality]}.jpg`;
};

export const addParams = (url: string, params: Record<string, string>) => {
  const hasQuery = url.includes("?");
  const query = new URLSearchParams(params).toString();
  return url + (hasQuery ? "&" : "?") + query;
};

export const getYtbVideoIframeUrl = (
  url: string,
  options?: {
    autoplay?: boolean;
    mute?: boolean;
    loop?: boolean;
  },
) => {
  const videoId = getYtbVideoId(url);
  if (!videoId) return url;
  const baseUrl =
    isTizen || isWebos
      ? `${YTB_PROXY_BASE_URL}/?v=${videoId}`
      : `https://www.youtube.com/embed/${videoId}`;

  const autoplay = !!options?.autoplay;
  const mute = !!options?.mute || (autoplay && isIOS);

  const params: Record<string, string> = {
    enablejsapi: "1",
    playsinline: "1",
  };

  if (isWebos) params.controls = "0";
  if (autoplay) params.autoplay = "1";
  if (mute) params.mute = "1";
  if (options?.loop) {
    params.loop = "1";
    params.playlist = videoId;
  }
  return addParams(baseUrl, params);
};

export type YtbPlayerCommand = "playVideo" | "pauseVideo";

export const sendYtbPlayerCommand = (
  iframe: HTMLIFrameElement | null | undefined,
  func: YtbPlayerCommand,
) => {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({ event: "command", func, args: [] }),
    "*",
  );
};

export const isImageUrl = (url: string) => {
  // Check if URL is an image by looking at common image extensions
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
    ".ico",
  ];

  const lowercaseUrl = url.toLowerCase();
  return imageExtensions.some((ext) => lowercaseUrl.includes(ext));
};

export const isVideoUrl = (url: string) => {
  // Check if URL is a video by looking at common video extensions
  const videoExtensions = [
    ".mp4",
    ".webm",
    ".ogg",
    ".mov",
    ".avi",
    ".mkv",
    ".m4v",
    ".flv",
    ".wmv",
  ];

  const lowercaseUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowercaseUrl.includes(ext));
};

export const getPegiImage = (pegi: number) => {
  switch (pegi) {
    case 3:
      return Pegi3;
    case 7:
      return Pegi7;
    case 12:
      return Pegi12;
    case 16:
      return Pegi16;
    case 18:
      return Pegi18;
    default:
      return Pegi18;
  }
};

export const requestFullscreenStream = (
  element?: HTMLElement,
  options?: { withPointerLock?: boolean },
) => {
  const targetEl = element ?? document.documentElement;
  const withPointerLock = options?.withPointerLock ?? true;

  const onFullscreenChange = () => {
    // Retry once fullscreen is active; some browsers allow pointer lock only then.
    if (withPointerLock) {
      requestPointerLockForPCGame();
    }
    cleanup();
  };

  const onWebkitFsChange = () => {
    if (withPointerLock) {
      requestPointerLockForPCGame();
    }
    cleanup();
  };

  const cleanup = () => {
    document.removeEventListener("fullscreenchange", onFullscreenChange);
    document.removeEventListener(
      "webkitfullscreenchange" as any,
      onWebkitFsChange as any,
    );
  };

  // Check if the call trigger from a user gesture
  const userActivationActive =
    typeof navigator !== "undefined" &&
    !!(navigator.userActivation && navigator.userActivation.isActive);

  // User gesture required
  if (withPointerLock && userActivationActive) {
    requestPointerLockForPCGame();
  }

  document.addEventListener("fullscreenchange", onFullscreenChange, {
    once: true,
  });
  document.addEventListener(
    "webkitfullscreenchange" as any,
    onWebkitFsChange as any,
    {
      once: true,
    },
  );

  const onError = (err: unknown) => {
    console.error(
      `[fullscreen] Failed to enable fullscreen: ${
        (err as any)?.message || String(err)
      }`,
    );
    cleanup();
  };

  try {
    if (
      "requestFullscreen" in targetEl &&
      typeof targetEl.requestFullscreen === "function"
    ) {
      const promise = targetEl.requestFullscreen();
      // Some browsers return a promise; chain a retry for pointer lock.
      if (promise && typeof (promise as any).then === "function") {
        (promise as Promise<void>)
          .then(() => {
            if (withPointerLock) {
              requestPointerLockForPCGame();
            }
          })
          .catch((err) => {
            onError(err);
          });
      } else {
        if (withPointerLock) {
          requestPointerLockForPCGame();
        }
      }
    } else if ((targetEl as any).webkitRequestFullscreen) {
      // Safari
      (targetEl as any).webkitRequestFullscreen();
      if (withPointerLock) {
        requestPointerLockForPCGame();
      }
    } else if ((targetEl as any).msRequestFullscreen) {
      // IE/Edge
      (targetEl as any).msRequestFullscreen();
      if (withPointerLock) {
        requestPointerLockForPCGame();
      }
    } else {
      console.warn(
        "[fullscreen] Fullscreen API not supported on this platform.",
      );
      cleanup();
    }
  } catch (err) {
    onError(err);
  }
};

export const exitFullscreenStream = () => {
  // Check if document has the exitFullscreen method
  if (document.exitFullscreen) {
    document.exitFullscreen(); // Standard way to exit fullscreen
  } else if (document.mozCancelFullScreen) {
    // Firefox
    document.mozCancelFullScreen();
  } else if (document.webkitExitFullscreen) {
    // Chrome, Safari, and Opera
    document.webkitExitFullscreen();
  } else if (document.msExitFullscreen) {
    // IE/Edge
    document.msExitFullscreen();
  }
};

export const getOnboardingImage = (
  screenSize?: ScreenSize,
  isActivation?: boolean,
) => {
  if (screenSize === "mobile") {
    return {
      bgSrc: MobileBg,
      src1x: MobileImage,
      src2x: MobileImage2x,
      src3x: MobileImage3x,
    };
  } else if (screenSize === "mobile-landscape") {
    return {
      bgSrc: MobileBg,
      src1x: LandscapeMobile,
      src2x: LandscapeMobile2x,
      src3x: LandscapeMobile3x,
    };
  } else if (screenSize === "tablet") {
    return {
      bgSrc: TabletBg,
      src1x: TabletImage,
      src2x: TabletImage2x,
      src3x: TabletImage3x,
    };
  } else if (isActivation) {
    return {
      bgSrc: ActivateBg,
      src1x: DesktopImage,
      src2x: DesktopImage2x,
      src3x: DesktopImage3x,
    };
  } else {
    return {
      bgSrc: DesktopBg,
    };
  }
};

export const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case "googlePlay":
      return {
        icon: GooglePlay,
        icon2x: GooglePlay2x,
        icon3x: GooglePlay3x,
        name: "Google Play",
        labelKey: "Download_Get On",
        typePopup: "google" as TypePopup,
      };
    case "appleStore":
      return {
        icon: Ios,
        icon2x: Ios2x,
        icon3x: Ios3x,
        name: "iOS - iPadOS",
        labelKey: "Download_Setup On",
        typePopup: "ios" as TypePopup,
      };
    case "appleStoreDesktop":
      return {
        icon: Ios,
        icon2x: Ios2x,
        icon3x: Ios3x,
        name: "macOS",
        labelKey: "Download_Setup On",
        typePopup: "osx" as TypePopup,
      };
    case "microsoftStore":
      return {
        icon: Microsoft,
        icon2x: Microsoft2x,
        icon3x: Microsoft3x,
        name: "Windows",
        labelKey: "Download_Setup On",
        typePopup: "windows" as TypePopup,
      };
    case "android":
      return {
        icon: GooglePlay,
        icon2x: GooglePlay2x,
        icon3x: GooglePlay3x,
        name: "Google Play",
        labelKey: "Download_Get On",
        typePopup: "android" as TypePopup,
      };
    case "lgTV":
      return {
        icon: Lg,
        icon2x: Lg2x,
        icon3x: Lg3x,
        name: "LG TV",
        labelKey: "Download_Get On",
        typePopup: "lg" as TypePopup,
      };
    case "amazon":
      return {
        icon: Amazon,
        icon2x: Amazon2x,
        icon3x: Amazon3x,
        name: "Amazon Fire TV",
        labelKey: "Download_Get On",
        typePopup: "amazon" as TypePopup,
      };
    default:
      return {
        icon: Samsung,
        icon2x: Samsung2x,
        icon3x: Samsung3x,
        name: "Samsung TV",
        labelKey: "Download_Get On",
        typePopup: "samsung" as TypePopup,
      };
  }
};

export const getSetupDevicesTitle = (type: TypePopup) => {
  switch (type) {
    case "android":
      return "SetupDevicesPopup_Setup On Android";
    case "windows":
      return "SetupDevicesPopup_Setup On Windows";
    case "ios":
      return "SetupDevicesPopup_Setup On iOS - iPadOS";
    default:
      return "SetupDevicesPopup_Setup On macOS";
  }
};
